import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { 
  Play, ArrowLeft, Check, Crown, Zap, 
  Video, Download, AlertCircle, CreditCard,
  Calendar, X
} from "lucide-react";

import { API_URL } from "../config/api";

const API = API_URL;

const SubscriptionPage = () => {
  const { user, getAuthHeaders, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingPlan, setProcessingPlan] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  useEffect(() => {
    fetchSubscription();
    fetchPlans();
    
    // Check for success redirect
    const sessionId = searchParams.get("session_id");
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, [fetchSubscription, fetchPlans, searchParams, checkPaymentStatus]);

  const fetchSubscription = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/subscription`, {
        headers: getAuthHeaders()
      });
      setSubscription(response.data);
    } catch (error) {
      // Subscription fetch failed - show subtle notification only on specific errors
      if (error.response?.status >= 500) {
        toast.error("Kon abonnement niet laden. Probeer later opnieuw.");
      }
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  const fetchPlans = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/subscription/plans`);
      setPlans(response.data.plans);
    } catch (error) {
      // Plans fetch failed - show error if server error
      if (error.response?.status >= 500) {
        toast.error("Kon abonnementen niet laden.");
      }
    }
  }, []);

  const checkPaymentStatus = useCallback(async (sessionId) => {
    try {
      const response = await axios.get(`${API}/subscription/status/${sessionId}`, {
        headers: getAuthHeaders()
      });
      
      if (response.data.payment_status === "paid") {
        toast.success("Betaling succesvol! Je abonnement is nu actief.");
        fetchSubscription();
        // Remove session_id from URL
        navigate("/subscription", { replace: true });
      }
    } catch (error) {
      // Payment check failed - notify user if persistent
      if (error.response?.status >= 500) {
        toast.error("Kon betalingsstatus niet controleren.");
      }
    }
  }, [getAuthHeaders, navigate, fetchSubscription]);

  const handleSubscribe = async (planId) => {
    setProcessingPlan(planId);
    
    try {
      const response = await axios.post(`${API}/subscription/checkout`, {
        plan: planId,
        origin_url: window.location.origin
      }, {
        headers: getAuthHeaders()
      });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error("Kon betaling niet starten. Probeer opnieuw.");
      setProcessingPlan(null);
    }
  };

  const handleCancelSubscription = async () => {
    if (!confirm("Weet je zeker dat je je abonnement wilt opzeggen?")) return;
    
    setCancelling(true);
    try {
      const response = await axios.post(`${API}/subscription/cancel`, {}, {
        headers: getAuthHeaders()
      });
      
      toast.success(response.data.message);
      fetchSubscription();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kon abonnement niet opzeggen");
    } finally {
      setCancelling(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("nl-NL", {
      day: "numeric",
      month: "long",
      year: "numeric"
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 dark-mode">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-900/90 backdrop-blur-md border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span>Terug naar Dashboard</span>
          </Link>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center">
              <Play className="w-4 h-4 text-white fill-white" />
            </div>
            <span className="font-semibold text-white">VideoMaker</span>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-6">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Abonnementen
            </h1>
            <p className="text-slate-400 text-lg">
              Kies het abonnement dat bij jou past
            </p>
          </div>

          {/* Current Subscription */}
          {subscription && (
            <Card className="bg-slate-900/80 border-slate-800 mb-8">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Crown className="w-5 h-5 text-amber-400" />
                  Huidig Abonnement
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-4 gap-6">
                  <div>
                    <p className="text-slate-400 text-sm">Plan</p>
                    <p className="text-white font-semibold text-lg">{subscription.plan_name}</p>
                  </div>
                  <div>
                    <p className="text-slate-400 text-sm">Video's deze maand</p>
                    <p className="text-white font-semibold text-lg">
                      {subscription.videos_created_this_month} / {subscription.max_videos === -1 ? "∞" : subscription.max_videos}
                    </p>
                  </div>
                  <div>
                    <p className="text-slate-400 text-sm">Max duur</p>
                    <p className="text-white font-semibold text-lg">{subscription.max_duration} sec</p>
                  </div>
                  <div>
                    <p className="text-slate-400 text-sm">Downloaden</p>
                    <p className="text-white font-semibold text-lg">
                      {subscription.can_download ? (
                        <span className="text-emerald-400 flex items-center gap-1">
                          <Check className="w-4 h-4" /> Ja
                        </span>
                      ) : (
                        <span className="text-red-400 flex items-center gap-1">
                          <X className="w-4 h-4" /> Nee
                        </span>
                      )}
                    </p>
                  </div>
                </div>

                {subscription.subscription_end_date && (
                  <div className="mt-6 pt-6 border-t border-slate-800 flex items-center justify-between">
                    <div className="flex items-center gap-2 text-slate-400">
                      <Calendar className="w-5 h-5" />
                      <span>Abonnement eindigt op: <strong className="text-white">{formatDate(subscription.subscription_end_date)}</strong></span>
                    </div>
                    <Button
                      variant="outline"
                      onClick={handleCancelSubscription}
                      disabled={cancelling}
                      className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                      data-testid="cancel-subscription-btn"
                    >
                      {cancelling ? "Bezig..." : "Abonnement Opzeggen"}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Demo Warning */}
          {subscription?.plan === "demo" && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-8 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-400 mt-0.5" />
              <div>
                <p className="text-amber-400 font-medium">Je gebruikt de Demo versie</p>
                <p className="text-slate-400 text-sm mt-1">
                  Met Demo kun je slechts 1 video van max 4 seconden maken en niet downloaden. 
                  Upgrade naar een abonnement voor meer mogelijkheden.
                </p>
              </div>
            </div>
          )}

          {/* Terms & Conditions */}
          <div className="mb-8 bg-slate-900/50 border border-slate-800 rounded-xl p-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                data-testid="terms-checkbox"
                className="mt-1 w-5 h-5 rounded border-slate-600 bg-slate-800 text-indigo-600 focus:ring-indigo-500 focus:ring-offset-slate-900"
              />
              <div className="text-sm">
                <span className="text-slate-300">
                  Ik ga akkoord met de{" "}
                  <button 
                    type="button"
                    onClick={() => toast.info(
                      <div className="max-w-sm">
                        <strong className="block mb-2">Gebruiksvoorwaarden</strong>
                        <p className="text-sm">
                          Door gebruik te maken van VideoMaker aanvaardt u dat alle gegenereerde video's op eigen risico worden gemaakt en gebruikt. 
                          VideoMaker is niet verantwoordelijk voor de inhoud van de door u aangeleverde materialen of de gegenereerde video's. 
                          U bent zelf verantwoordelijk voor het naleven van auteursrechten, merkrechten en overige intellectuele eigendomsrechten. 
                          VideoMaker kan niet aansprakelijk worden gesteld voor directe of indirecte schade voortvloeiend uit het gebruik van de dienst.
                        </p>
                      </div>,
                      { duration: 10000 }
                    )}
                    className="text-indigo-400 hover:text-indigo-300 underline"
                  >
                    algemene voorwaarden
                  </button>
                  {" "}en bevestig dat ik begrijp dat alle gegenereerde video's op eigen verantwoordelijkheid worden gemaakt en gebruikt.
                </span>
              </div>
            </label>
            {!termsAccepted && (
              <p className="mt-2 text-xs text-amber-400 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                Je moet akkoord gaan met de voorwaarden om een abonnement af te sluiten
              </p>
            )}
          </div>

          {/* Plans */}
          <div className="grid md:grid-cols-2 gap-6">
            {plans.map((plan, index) => (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className={`bg-slate-900/80 border-slate-800 hover:border-slate-700 transition-all h-full ${
                  plan.id === "pro" ? "ring-2 ring-indigo-500/50" : ""
                }`}>
                  <CardHeader>
                    {plan.id === "pro" && (
                      <Badge className="w-fit mb-2 bg-indigo-500/20 text-indigo-400 border-indigo-500/30">
                        Meest gekozen
                      </Badge>
                    )}
                    <CardTitle className="text-white text-2xl flex items-center gap-2">
                      {plan.id === "pro" ? <Zap className="w-6 h-6 text-indigo-400" /> : <Video className="w-6 h-6 text-slate-400" />}
                      {plan.name}
                    </CardTitle>
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl font-bold text-white">€{plan.price}</span>
                      <span className="text-slate-400">/maand</span>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3 mb-6">
                      {plan.features.map((feature, i) => (
                        <li key={`${plan.id}-feature-${i}`} className="flex items-center gap-2 text-slate-300">
                          <Check className="w-5 h-5 text-emerald-400" />
                          {feature}
                        </li>
                      ))}
                    </ul>

                    <Button
                      onClick={() => handleSubscribe(plan.id)}
                      disabled={processingPlan === plan.id || subscription?.plan === plan.id || !termsAccepted}
                      data-testid={`subscribe-${plan.id}-btn`}
                      className={`w-full py-6 ${
                        plan.id === "pro" 
                          ? "bg-indigo-600 hover:bg-indigo-700 text-white disabled:bg-indigo-600/50"
                          : "bg-slate-800 hover:bg-slate-700 text-white disabled:bg-slate-800/50"
                      }`}
                    >
                      {processingPlan === plan.id ? (
                        <div className="flex items-center gap-2">
                          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                          Laden...
                        </div>
                      ) : subscription?.plan === plan.id ? (
                        "Huidig Plan"
                      ) : (
                        <>
                          <CreditCard className="w-5 h-5 mr-2" />
                          Kies {plan.name}
                        </>
                      )}
                    </Button>

                    <p className="text-xs text-slate-500 text-center mt-3">
                      Betaal met iDEAL of creditcard
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* FAQ */}
          <div className="mt-12">
            <h2 className="text-2xl font-semibold text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Veelgestelde Vragen
            </h2>
            <div className="space-y-4">
              <Card className="bg-slate-900/50 border-slate-800">
                <CardContent className="p-4">
                  <h3 className="text-white font-medium mb-1">Kan ik op elk moment opzeggen?</h3>
                  <p className="text-slate-400 text-sm">
                    Ja, je kunt je abonnement op elk moment opzeggen. Je houdt toegang tot het einde van de betaalde periode.
                  </p>
                </CardContent>
              </Card>
              <Card className="bg-slate-900/50 border-slate-800">
                <CardContent className="p-4">
                  <h3 className="text-white font-medium mb-1">Welke betaalmethodes worden geaccepteerd?</h3>
                  <p className="text-slate-400 text-sm">
                    We accepteren iDEAL, creditcard (Visa, Mastercard), en PayPal.
                  </p>
                </CardContent>
              </Card>
              <Card className="bg-slate-900/50 border-slate-800">
                <CardContent className="p-4">
                  <h3 className="text-white font-medium mb-1">Wat gebeurt er als mijn limiet is bereikt?</h3>
                  <p className="text-slate-400 text-sm">
                    Je kunt upgraden naar het Pro abonnement voor meer video's (max 10 per maand), of wachten tot de volgende maand wanneer je limiet reset.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SubscriptionPage;
